import { all, call, put, takeLatest } from 'redux-saga/effects';
import { APIResponse } from 'spw-redux/types';
import { handleError } from '../UI/messages/actions';
import {
  getJoinRequestsForAllTeams as getJoinRequestsForAllTeamsApi,
  getUserPendingTeamRequests as getUserPendingTeamRequestsApi,
  getUserPendingProjectRequests as getUserPendingProjectRequestsApi,
  getJoinRequestsForTeam as getJoinRequestsForTeamApi,
  getProjectJoinRequests as getProjectJoinRequestsApi,
  acceptJoinRequest as acceptJoinRequestApi,
  rejectJoinRequest as rejectJoinRequestApi,
  getUserProjectsJoinRequests,
} from '../../../api/join-requests';
import {
  actions,
  actionNames,
  getUserPendingProjectRequests,
  getProjectJoinRequests,
  acceptJoinRequest,
  rejectJoinRequest,
} from './actions';
import {
  JoinRequestListItemVM,
  JoinRequestSlimForList,
  JoinRequestStatus,
} from './types';

const getJoinRequestsListVM = (
  request: JoinRequestSlimForList
): JoinRequestListItemVM => ({
  ...request,
});

function* getUserPendingTeamRequests(
  _action: ReturnType<typeof actions.getUserPendingTeamRequests>
) {
  try {
    const response: APIResponse<typeof getUserPendingTeamRequestsApi> = yield call(
      getUserPendingTeamRequestsApi
    );

    yield put(
      actions.getUserPendingTeamRequestsSucceeded(
        response.data.joinRequests
          .filter((jr) => jr.teamId)
          .reduce((map, request) => ({ ...map, [request.teamId]: request }), {})
      )
    );
  } catch (e) {
    yield put(handleError(e));
  }
}

function* getJoinRequestsForAllTeams(
  action: ReturnType<typeof actions.getJoinRequestsForAllTeams>
) {
  try {
    const response: APIResponse<typeof getJoinRequestsForAllTeamsApi> = yield call(
      getJoinRequestsForAllTeamsApi,
      action.payload
    );

    yield put(
      actions.getJoinRequestsForAllTeamsSucceeded({
        total: response.data.results.total,
        items: response.data.results.joinRequests.map((joinRequest) =>
          getJoinRequestsListVM(joinRequest)
        ),
        shouldAppend: action.payload.page > 1,
      })
    );
  } catch (e) {
    yield put(actions.getJoinRequestsForAllTeamsFailed());
    yield put(handleError(e));
  }
}

function* getUserPendingProjectRequestsCall(
  action: ReturnType<typeof getUserPendingProjectRequests>
) {
  try {
    const response: APIResponse<typeof getUserPendingProjectRequestsApi> = yield call(
      getUserPendingProjectRequestsApi,
      action.payload.projectId
    );

    yield put(
      actions.getUserPendingProjectRequestsSucceeded(
        response.data.joinRequests.reduce(
          (map, request) => ({ ...map, [request.projectId]: request }),
          {}
        )
      )
    );
  } catch (e) {
    yield put(actions.getUserPendingProjectRequestsFailed());
    yield put(handleError(e));
  }
}

function* getJoinRequestsForTeam(
  action: ReturnType<typeof actions.getJoinRequestsForTeam>
) {
  try {
    const response: APIResponse<typeof getJoinRequestsForTeamApi> = yield call(
      getJoinRequestsForTeamApi,
      action.payload.id,
      action.payload.params
    );

    yield put(
      actions.getJoinRequestsForTeamSucceeded({
        joinRequests: response.data.results.joinRequests.map((joinRequest) =>
          getJoinRequestsListVM(joinRequest)
        ),
        total: response.data.results.total,
        shouldAppend: action.payload.params.page > 1,
      })
    );
  } catch (e) {
    yield put(actions.getJoinRequestsForTeamFailed());
    yield put(handleError(e));
  }
}

function* getProjectJoinRequestsCall(
  action: ReturnType<typeof getProjectJoinRequests>
) {
  try {
    const response: APIResponse<typeof getProjectJoinRequestsApi> = yield call(
      getProjectJoinRequestsApi,
      action.payload
    );
    yield put(
      actions.getProjectJoinRequestsSucceeded({
        joinRequests: response.data.results.joinRequests,
        total: response.data.results.total,
        shouldAppend: action.payload.page > 1,
      })
    );
  } catch (e) {
    yield put(actions.getProjectJoinRequestsFailed());
    yield put(handleError(e));
  }
}

function* acceptJoinRequestsCall(action: ReturnType<typeof acceptJoinRequest>) {
  try {
    const response: APIResponse<typeof acceptJoinRequestApi> = yield call(
      acceptJoinRequestApi,
      action.payload.id
    );
    yield put(actions.acceptJoinRequestSucceeded());
    if (action.payload.onRequestCompleted)
      action.payload.onRequestCompleted(
        response.data.joinRequest.status === JoinRequestStatus.approved
      );
  } catch (e) {
    yield put(actions.acceptJoinRequestFailed());
    yield put(handleError(e));
  }
}

function* rejectJoinRequestsCall(action: ReturnType<typeof rejectJoinRequest>) {
  try {
    const response: APIResponse<typeof rejectJoinRequestApi> = yield call(
      rejectJoinRequestApi,
      action.payload.id
    );
    yield put(actions.rejectJoinRequestSucceeded());
    if (action.payload.onRequestCompleted)
      action.payload.onRequestCompleted(
        response.data.joinRequest.status === JoinRequestStatus.rejected
      );
  } catch (e) {
    yield put(actions.rejectJoinRequestFailed());
    yield put(handleError(e));
  }
}

function* getUserProjectsJoinRequestsCall(
  action: ReturnType<typeof actions.getUserProjectsJoinRequests>
) {
  try {
    const response: APIResponse<typeof getUserProjectsJoinRequests> = yield call(
      getUserProjectsJoinRequests,
      action.payload
    );
    yield put(
      actions.getUserProjectsJoinRequestsSucceeded({
        total: response.data.results.total,
        items: response.data.results.joinRequests,
        shouldAppend: action.payload.page > 1,
      })
    );
  } catch (e) {
    yield put(actions.getUserProjectsJoinRequestsFailed());
    yield put(handleError(e));
  }
}

function* getJoinRequestsForNotifications(
  _: ReturnType<typeof actions.getJoinRequestsForNotifications>
) {
  try {
    const payload = { page: 1, perPage: 0 };
    const [projectResponse, teamResponse]: [
      APIResponse<typeof getUserProjectsJoinRequests>,
      APIResponse<typeof getJoinRequestsForAllTeamsApi>
    ] = yield all([
      call(getUserProjectsJoinRequests, payload),
      call(getJoinRequestsForAllTeamsApi, payload),
    ]);

    yield put(
      actions.getJoinRequestsForNotificationsSucceeded({
        projectRequestsTotal: projectResponse.data.results.total,
        teamRequestsTotal: teamResponse.data.results.total,
      })
    );
  } catch (e) {
    yield put(handleError(e));
  }
}

function* saga() {
  yield all([
    takeLatest(
      actionNames.GET_JOIN_REQUESTS_FOR_ALL_TEAMS,
      getJoinRequestsForAllTeams
    ),
    takeLatest(
      actionNames.GET_USER_PENDING_TEAM_REQUESTS,
      getUserPendingTeamRequests
    ),
    takeLatest(
      actionNames.GET_USER_PENDING_PROJECT_REQUESTS,
      getUserPendingProjectRequestsCall
    ),
    takeLatest(actionNames.GET_JOIN_REQUESTS_FOR_TEAM, getJoinRequestsForTeam),
    takeLatest(
      actionNames.GET_PROJECT_JOIN_REQUESTS,
      getProjectJoinRequestsCall
    ),
    takeLatest(actionNames.ACCEPT_JOIN_REQUEST, acceptJoinRequestsCall),
    takeLatest(actionNames.REJECT_JOIN_REQUEST, rejectJoinRequestsCall),
    takeLatest(
      actionNames.GET_USER_PROJECTS_JOIN_REQUESTS,
      getUserProjectsJoinRequestsCall
    ),
    takeLatest(
      actionNames.GET_JOIN_REQUESTS_FOR_NOTIFICATIONS,
      getJoinRequestsForNotifications
    ),
  ]);
}

export default saga;
